import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const TwoFactorAuth = ({ onSuccess, onBack }) => {
  const { verify2FA, resend2FA } = useAuth();
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [skip2FA, setSkip2FA] = useState(false);
  const inputRefs = useRef([]);

  useEffect(() => {
    // Start resend timer
    const timer = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleCodeInput = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Move to next input if current is filled
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if code is complete
    if (newCode.every(digit => digit !== '')) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Move to previous input if backspace is pressed and current is empty
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const numbers = pastedData.replace(/[^\d]/g, '').slice(0, 6);
    
    if (numbers.length === 6) {
      const newCode = numbers.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (fullCode) => {
    setLoading(true);
    setError('');

    try {
      await verify2FA(fullCode, skip2FA);
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err) {
      if (err.code === '2FA_EXPIRED') {
        setError('2FA session expired. Please login again.');
        onBack();
      } else {
        setError(err.message);
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!canResend) return;

    setLoading(true);
    setError('');

    try {
      await resend2FA();
      setSuccess('2FA code resent to your email');
      setCanResend(false);
      setResendTimer(30);
      
      // Restart timer
      const timer = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      if (err.code === '2FA_EXPIRED') {
        setError('2FA session expired. Please login again.');
        onBack();
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const isCodeComplete = code.every(digit => digit !== '');

  return (
    <div className="login-container">
      <div className="login-card">
        <h2 className="twofa-title">Two-Factor Authentication</h2>
        <p className="twofa-description">
          Please enter the 6-digit verification code sent to your email address.
        </p>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <div className="code-input-container">
          {code.map((digit, index) => (
            <input
              key={index}
              ref={el => inputRefs.current[index] = el}
              type="text"
              className={`code-input ${digit ? 'filled' : ''} ${error ? 'error' : ''}`}
              maxLength="1"
              value={digit}
              onChange={(e) => handleCodeInput(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              autoComplete="off"
              disabled={loading}
            />
          ))}
        </div>
        
        <button 
          type="button" 
          className="btn btn-primary" 
          style={{ background: 'var(--primary-color)', color: 'var(--secondary-color)', fontWeight: 700 }}
          onClick={() => handleSubmit(code.join(''))}
          disabled={loading || !isCodeComplete}
        >
          {loading ? 'Verifying...' : 'Verify Code'}
        </button>
        
        <div className="form-group checkbox-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={skip2FA}
              onChange={(e) => setSkip2FA(e.target.checked)}
              disabled={loading}
            />
            <span>Remember this device for 30 days</span>
          </label>
        </div>

        <div className="form-footer">
          <button 
            type="button" 
            onClick={handleResend} 
            className="btn btn-link"
            disabled={loading || !canResend}
          >
            Resend Code
          </button>
          <button 
            type="button" 
            onClick={onBack} 
            className="btn btn-link"
            disabled={loading}
          >
            Back to Login
          </button>
        </div>
        
        {!canResend && resendTimer > 0 && (
          <div className="timer">
            Resend available in {resendTimer} seconds
          </div>
        )}
      </div>
    </div>
  );
};

export default TwoFactorAuth; 